import { useEffect, useMemo, useState, type CSSProperties, type MouseEvent } from "react";
import type { Media } from "@bronte/viewer";
import type { Component, StageElement, ThemeVariant, ViewerDeviceRuntime } from "../../../types";
import { LabelSection, InstructionsBlock } from "../../styles";
import { inferBronteThemeWrapperClass } from "../../../util/bronteThemeClass";
import {
  etextMcqIconCorrect,
  etextMcqIconIncorrect,
  MCQ_SUBMIT_BUTTON_LABEL,
  MCQ_TRY_AGAIN_BUTTON_LABEL,
  RESPONSE_HEADER_CORRECT,
  RESPONSE_HEADER_INCORRECT,
} from "./feedbackConstants";
import {
  McqImageBlock,
  choiceIsSelected,
  correctAnswersSelected,
  isMcqAnnotationTarget,
  normalizeMcqChoices,
  parsePluginConfig,
  type McqChoice,
} from "./etextMcqPluginSupport";
import "./styles.scss";

// BV hands the plugin the theme variant config through theme(), and the plugin turns its
// fonts into --body-font / --title-font (answers / question prompt). Mirror that so the
// MCQ uses the activity fonts (e.g. system-ui) instead of inheriting the book font.
function themeFontVars(themeVariant: Partial<ThemeVariant> | undefined): CSSProperties {
  const { bodyFont, titleFont } = (themeVariant ?? {}) as { bodyFont?: string; titleFont?: string };
  const vars: Record<string, string> = {};
  if (bodyFont) vars["--body-font"] = bodyFont;
  if (titleFont) vars["--title-font"] = titleFont;
  return vars as CSSProperties;
}

export type EtextMcqPluginComponentProps = {
  element: StageElement;
  componentConfig: Component;
  onMediaClick?: (data: Media) => void;
  themeVariant?: Partial<ThemeVariant>;
  viewerDevice?: ViewerDeviceRuntime;
};

export function EtextMcqPluginComponent({
  componentConfig,
  onMediaClick,
  themeVariant,
  viewerDevice,
}: EtextMcqPluginComponentProps) {
  const cfg = parsePluginConfig(componentConfig.config);
  const {
    activityLabel,
    activityTitle,
    instructions,
    label = "",
    questionImage,
    buttonPosition = "left",
    allowMultiSelect = false,
    choices: rawChoices = [],
    cssClass,
  } = cfg;

  const normalizedChoices = useMemo(
    (): McqChoice[] => normalizeMcqChoices(rawChoices),
    [rawChoices],
  );

  const [singleSelected, setSingleSelected] = useState<string | null>(null);
  const [multiSelected, setMultiSelected] = useState<string[]>([]);
  const [isShowFeedback, setIsShowFeedback] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  const componentId = componentConfig.$id || "";
  const inputGroupName = `mcq-group-${componentId}`;

  const selectedLabels = allowMultiSelect ? multiSelected : singleSelected ? [singleSelected] : [];

  const selectedItems = useMemo(
    () => normalizedChoices.filter((c) => selectedLabels.includes(c.choiceLabel)),
    [normalizedChoices, selectedLabels],
  );

  const correctItems = useMemo(
    () => normalizedChoices.filter((c) => c.correct),
    [normalizedChoices],
  );

  useEffect(() => {
    if (correctAnswersSelected(correctItems, selectedItems) && isShowFeedback) {
      setIsLocked(true);
    }
  }, [correctItems, selectedItems, isShowFeedback]);

  const onToggleChoice = (choiceLabel: string, checked: boolean) => {
    setIsShowFeedback(false);
    setIsSubmitted(false);
    if (allowMultiSelect) {
      setMultiSelected((prev) =>
        checked ? [...prev, choiceLabel] : prev.filter((l) => l !== choiceLabel),
      );
    } else {
      setSingleSelected(choiceLabel);
    }
  };

  const onSubmit = () => {
    const hasSelection = allowMultiSelect ? multiSelected.length > 0 : singleSelected != null;
    if (hasSelection) {
      setIsShowFeedback((v) => !v);
      setIsSubmitted(true);
    }
  };

  const onTryAgain = () => {
    if (allowMultiSelect) {
      setMultiSelected([]);
    } else {
      setSingleSelected(null);
    }
    setIsSubmitted(false);
  };

  const onOptionClick = (event: MouseEvent, choiceLabel: string) => {
    if (isLocked || isMcqAnnotationTarget(event.target)) {
      return;
    }

    if (allowMultiSelect) {
      onToggleChoice(choiceLabel, !multiSelected.includes(choiceLabel));
      return;
    }

    onToggleChoice(choiceLabel, true);
  };

  const showFeedbackForChoice = (choiceLabel: string) =>
    isShowFeedback &&
    choiceIsSelected(allowMultiSelect, choiceLabel, singleSelected, multiSelected);

  const bronteThemeWrapper = inferBronteThemeWrapperClass(themeVariant);

  // plugin-eText-MCQ is the BV plugin iframe's body class: theme rules written against it
  // (body.plugin-eText-MCQ label.option ...) are scoped onto this wrapper by cssProcessor
  return (
    <div
      className={`etext-mcq-plugin plugin-etext-mcq plugin-eText-MCQ ${bronteThemeWrapper} ${
        cssClass ?? ""
      }`.trim()}
      style={themeFontVars(themeVariant)}
      role={allowMultiSelect ? "group" : "radiogroup"}
      tabIndex={0}
      aria-labelledby={`${componentId}-question`}
      data-id={componentId}
    >
      <div className="question">
        {(activityTitle || activityLabel || instructions) && (
          <div className="etext-mcq-plugin__label-block">
            {(activityLabel || activityTitle) && (
              <LabelSection className="component-label-section">
                {activityLabel ? (
                  <span
                    className="component-label"
                    data-id={`${componentId}-activityLabel`}
                    dangerouslySetInnerHTML={{ __html: activityLabel }}
                  />
                ) : null}
                {activityTitle ? (
                  <span
                    className="component-title"
                    data-id={`${componentId}-activityTitle`}
                    dangerouslySetInnerHTML={{ __html: activityTitle }}
                  />
                ) : null}
              </LabelSection>
            )}
            {instructions ? (
              <InstructionsBlock
                className="component-instructions"
                data-id={`${componentId}-instructions`}
                dangerouslySetInnerHTML={{ __html: instructions }}
              />
            ) : null}
          </div>
        )}
        <div
          id={`${componentId}-question`}
          className="question-prompt"
          data-id={`${componentId}-label`}
        >
          {label ? <span dangerouslySetInnerHTML={{ __html: label }} /> : null}
          <McqImageBlock
            image={questionImage && questionImage !== "" ? questionImage : undefined}
            variant="question"
            dataId={`${componentId}-questionImage`}
            viewerDevice={viewerDevice}
          />
        </div>
      </div>

      <div className="answer-options">
        {normalizedChoices.map((opt, position) => {
          const feedbackShowing = showFeedbackForChoice(opt.choiceLabel);
          const responseHeader = opt.correct ? RESPONSE_HEADER_CORRECT : RESPONSE_HEADER_INCORRECT;

          return (
            <div
              className="mcq-answer mcq-answer--option answer"
              key={`${opt.choiceLabel}-${position}`}
            >
              {feedbackShowing ? (
                <div
                  className={`mcq-feedback-icon feedback-icon ${
                    opt.correct
                      ? "mcq-feedback-icon--correct correct"
                      : "mcq-feedback-icon--incorrect incorrect"
                  }`}
                  dangerouslySetInnerHTML={{
                    __html: opt.correct ? etextMcqIconCorrect : etextMcqIconIncorrect,
                  }}
                />
              ) : (
                <input
                  id={`${componentId}-opt-${position}`}
                  name={inputGroupName}
                  className={`mcq-selector selector ${
                    allowMultiSelect ? "mcq-selector--checkbox checkbox" : ""
                  }`}
                  type={allowMultiSelect ? "checkbox" : "radio"}
                  checked={choiceIsSelected(
                    allowMultiSelect,
                    opt.choiceLabel,
                    singleSelected,
                    multiSelected,
                  )}
                  disabled={isLocked}
                  onChange={(e) => onToggleChoice(opt.choiceLabel, e.target.checked)}
                  aria-disabled={isLocked}
                  aria-labelledby={`${componentId}-opt-label-${position}`}
                />
              )}
              {/* <label> like BV's plugin so theme rules on label.option apply; no htmlFor
                  and no nested control, so it has no native activation besides onClick */}
              <label
                id={`${componentId}-opt-label-${position}`}
                className={`mcq-option option ${isLocked ? "mcq-option--locked" : ""}`}
                data-id={`${componentId}-choiceLabel-${position}`}
                onClick={(event) => onOptionClick(event, opt.choiceLabel)}
                dangerouslySetInnerHTML={{ __html: opt.choiceLabel }}
              />
              <McqImageBlock
                image={opt.choiceImage}
                variant="choice"
                dataId={`${componentId}-choiceImage-${position}`}
                viewerDevice={viewerDevice}
              />

              <div
                className={`mcq-response response ${
                  opt.correct
                    ? "correct mcq-response--correct"
                    : "incorrect mcq-response--incorrect"
                } ${feedbackShowing ? "" : "mcq-response--hidden"}`.trim()}
              >
                <span
                  className="mcq-response-header response-header"
                  dangerouslySetInnerHTML={{ __html: responseHeader }}
                />
                {opt.feedback ? (
                  <div
                    className="mcq-feedback-text feedback"
                    data-id={`${componentId}-feedback-${position}`}
                    dangerouslySetInnerHTML={{ __html: opt.feedback }}
                  />
                ) : null}
                <McqImageBlock
                  image={opt.feedbackImage}
                  variant="feedback"
                  dataId={`${componentId}-feedbackImage-${position}`}
                  onMediaClick={onMediaClick}
                  viewerDevice={viewerDevice}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div
        className="button-row"
        style={{
          justifyContent:
            buttonPosition === "center"
              ? "center"
              : buttonPosition === "right"
              ? "flex-end"
              : "flex-start",
        }}
      >
        {!isLocked && !isSubmitted ? (
          <button type="button" tabIndex={0} onClick={onSubmit}>
            {MCQ_SUBMIT_BUTTON_LABEL}
          </button>
        ) : null}
        {!isLocked && isSubmitted ? (
          <button type="button" tabIndex={0} onClick={onTryAgain}>
            {MCQ_TRY_AGAIN_BUTTON_LABEL}
          </button>
        ) : null}
      </div>
    </div>
  );
}
